/**
 * AI Server 통합 인터페이스 테스트용 호출 정의 — **샘플 요청 바디는 전부 여기에 둔다.**
 *
 * 화면(AiRefCallTestPanel)에는 로직만 있고 데이터는 이 파일에만 있다. 규격이 바뀌면
 * 이 파일만 고치면 되고, 화면 쪽 코드를 통째로 갈아끼워도 샘플은 덮이지 않는다.
 *
 * 카드를 추가하려면 CALL_DEFS에 항목 하나를 넣으면 된다. group 값에 따라 자동으로
 * 해당 섹션에 렌더된다.
 */

export type CallGroup = 'chat' | 'info' | 'inference';

export interface CallDef {
  /** 화면 내부 식별자 — 응답 보관 키로도 쓰인다 */
  key: string;
  group: CallGroup;
  title: string;
  url: string;
  /** 요청 바디 샘플. 화면에서 고칠 수 있고, [샘플 복원]은 이 값으로 되돌린다 */
  body: string;
  /** SSE로 흘러오는 응답을 스트림으로 읽는다 */
  stream?: boolean;
  /** AI 응답이 길어 타임아웃을 크게 잡는다 */
  slow?: boolean;
}

export const GROUPS: CallGroup[] = ['chat', 'info', 'inference'];

export const GROUP_TITLES: Record<CallGroup, string> = {
  chat: '② chat — 챗봇',
  info: '③ info — 마스터 / 데이터 / 공장',
  inference: '④ inference — 리포트 생성',
};

const HINT_CHAT = '';

const HINT_INFO = '';

const HINT_REPORT = 'AI 응답이 길어 타임아웃을 240초로 둡니다.';

export const GROUP_HINTS: Record<CallGroup, string> = {
  chat: HINT_CHAT,
  info: HINT_INFO,
  inference: HINT_REPORT,
};

/**
 * info 3종의 info_ctn 예시는 BE가 AI 서버로 올려보내는 형태 그대로다. FE는 적재
 * 트리거만 보내면 되는 구조라 BE가 본문을 무시할 수도 있는데, 규격 확인용으로 남겨둔다.
 * session_id는 chat에서만 쓴다 — info·inference 바디에는 넣지 않는다.
 */
export const CALL_DEFS: CallDef[] = [
  {
    key: 'chatNormal',
    group: 'chat',
    title: '일반챗',
    url: '/v1/chat',
    stream: true,
    body: `{
  "chatType": "chat",
  "question": "확정완료된 UT Matrix는 공장별로 몇 개야?",
  "sessionId": ""
}`,
  },
  {
    key: 'chatScoped',
    group: 'chat',
    title: '범위챗',
    url: '/v1/chat',
    stream: true,
    body: `{
  "chatType": "chat",
  "question": "GM2 공장 사용량 알려줘",
  "sessionId": "",
  "scopeType": "matrix_list",
  "scopeCtn": [{ "utmId": "UTM00421", "verNo": "1.13", "bildPlntCd": "GM2" }]
}`,
  },
  {
    key: 'chatCancel',
    group: 'chat',
    title: '챗 중단',
    url: '/v1/chat',
    body: `{
  "chatType": "cancel",
  "sessionId": ""
}`,
  },
  {
    key: 'infoMaster',
    group: 'info',
    title: '정보 — 마스터 (ut_master)',
    url: '/v1/ut/ai-ref/send-master',
    body: `{"utmId": "UTM00321"}`,
  },
  {
    key: 'infoUtData',
    group: 'info',
    title: '정보 — 데이터 (ut_data)',
    url: '/v1/ut/ai-ref/send-ut-data',
    body: `{"utmId": "UTM00321"}`,
  },
  {
    key: 'infoPlant',
    group: 'info',
    title: '정보 — 공장 (ut_plant)',
    url: '/v1/ut/ai-ref/send-plant',
    body: ``,
  },
  {
    key: 'reportDraft',
    group: 'inference',
    title: '추론 — 리포트 초안',
    url: '/v1/ut/ai-ref/report',
    slow: true,
    body: `{
  "utmId": "UTM00164,UTM00561",
  "reportDomain": "mech",
  "reportId": "RPT-00162",
  "reportName": "20260915 AZ1-03(1.0), WA1-01(1.0) 비교분석"
}`,
  },
  {
    key: 'reportFinal',
    group: 'inference',
    title: '추론 — 최종 리포트',
    url: '/v1/ut/ai-ref/report/final',
    slow: true,
    body: `{
  "if_id": "",
  "ifrc_type": "final_report",
  "report_id": "RPT-00162",
  "report_nm": "20260915 AZ1-03(1.0), WA1-01(1.0) 비교분석",
  "report_matrix": {
    "base": { "utmId": "UTM00561" },
    "compared": { "utmId": "UTM00164" }
  },
  "user_content": { "mech": "", "elec": "" }, 
  "ai_content": {
    "mech": { "sections": [] },
    "elec": { "sections": [] }
  }
}`,
  },
];

/** 편집기를 원본 샘플로 되돌린다 */
export const getSampleBody = (key: string): string =>
  CALL_DEFS.find((call) => call.key === key)?.body ?? '{}';
